import * as Plot from "@observablehq/plot";
import { useRef, useEffect } from "react";

const applyTooltipTheme = (root) => {
    if (!root) {
        return {};
    }

    const doc = root.ownerDocument || document;
    const tipVars = doc.documentElement?.style;
    if (tipVars) {
        tipVars.setProperty('--plot-tooltip-background', 'rgba(255, 255, 255, 0.96)');
        tipVars.setProperty('--plot-tooltip-color', '#0c0c0c');
        tipVars.setProperty('--plot-tooltip-border', '1px solid rgba(15, 0, 60, 0.24)');
        tipVars.setProperty('--plot-tooltip-border-radius', '16px');
        tipVars.setProperty('--plot-tooltip-box-shadow', '0 24px 48px rgba(9, 0, 26, 0.25)');
        tipVars.setProperty('--plot-tooltip-backdrop-filter', 'blur(14px)');
    }

    const styleTooltip = (tooltip) => {
        if (!(tooltip instanceof HTMLElement)) {
            return;
        }
        tooltip.style.background = 'rgba(255, 255, 255, 0.96)';
        tooltip.style.color = '#0c0c0c';
        tooltip.style.border = '1px solid rgba(15, 0, 60, 0.24)';
        tooltip.style.borderRadius = '16px';
        tooltip.style.boxShadow = '0 24px 48px rgba(9, 0, 26, 0.25)';
        tooltip.style.backdropFilter = 'blur(14px)';
        tooltip.style.padding = '0.75rem 1rem';
        tooltip.style.fontFamily = "'Space Grotesk', 'Inter', sans-serif";
        tooltip.style.letterSpacing = '0.08em';
        tooltip.style.textTransform = 'uppercase';
    };

    const prime = (container) => {
        if (!container) {
            return;
        }
        container.querySelectorAll('.plot-tooltip').forEach(styleTooltip);
    };

    prime(root);
    prime(doc.body);

    const rootObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    if (node.classList.contains('plot-tooltip')) {
                        styleTooltip(node);
                    } else {
                        node.querySelectorAll?.('.plot-tooltip').forEach(styleTooltip);
                    }
                }
            });
        });
    });
    rootObserver.observe(root, { childList: true, subtree: true });

    let bodyObserver;
    if (doc.body && doc.body !== root) {
        bodyObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        if (node.classList.contains('plot-tooltip')) {
                            styleTooltip(node);
                        } else {
                            node.querySelectorAll?.('.plot-tooltip').forEach(styleTooltip);
                        }
                    }
                });
            });
        });
        bodyObserver.observe(doc.body, { childList: true, subtree: true });
    }

    return { rootObserver, bodyObserver };
};

export default function PlotFigure({ options, height = 400, className, style }) {
    const containerRef = useRef();

    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return undefined;
        }

        // Clear previous plot
        node.innerHTML = '';

        // Create plot with real DOM and enhanced interactivity
        const plot = Plot.plot({
            ...options,
            marks: options.marks ? [...options.marks] : options.marks,
        });

        const tooltipObservers = applyTooltipTheme(plot);

        // Append to container
        node.appendChild(plot);

        // Cleanup function
        return () => {
            tooltipObservers?.rootObserver?.disconnect?.();
            tooltipObservers?.bodyObserver?.disconnect?.();
            node.innerHTML = '';
        };
    }, [options]);

    const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
    const combinedStyle = {
        width: '100%',
        height: resolvedHeight || '400px',
        ...style,
    };

    const classes = ['plot-figure', className].filter(Boolean).join(' ');

    return <div ref={containerRef} className={classes} style={combinedStyle} />;
}